import { IonButton, IonIcon, IonSkeletonText, IonText, useIonRouter } from "@ionic/react";
import { useTranslation } from "react-i18next";
import { useQuery } from "@tanstack/react-query";
import { CHOSEN_CORE_VALUES_LIMIT, FRIEND_CHECK_TARGET } from "@/lib/constants";
import { useFriendCheck } from "@/lib/hooks/useFriendcheck";
import { arrowRightIcon } from "@/lib/icons/@heroicons/react/16/solid";
import { queryOptions_coreValuesChosen } from "@/lib/query/functions/core-values/chosen";
import { queryOptions_friendCheckSubmissions } from "@/lib/query/functions/friend-check/submissions";
import { useMyData } from "@/lib/query/functions/user/data";
import { ProgressBar } from "../Global/ProgressBar";

export function FriendCheckWidget() {
	const { t } = useTranslation();
	const router = useIonRouter();
	const [myData] = useMyData();
	const friendCheckQuery = useQuery(queryOptions_friendCheckSubmissions());
	const { service: friendCheckService, isLoading: isFriendCheckLoading } = useFriendCheck();
	const { data: coreValues, isLoading: isCoreValuesLoading } = useQuery(
		queryOptions_coreValuesChosen(),
	);

	if (friendCheckQuery.isLoading || isFriendCheckLoading || isCoreValuesLoading) {
		return <IonSkeletonText animated className="h-42 rounded-3xl" />;
	}

	const coreValuesCount = coreValues?.length ?? 0;
	const all = friendCheckQuery.data?.length ?? 0;

	const hasExceededTarget = all > FRIEND_CHECK_TARGET;

	const progressLabel = hasExceededTarget
		? t("widgets.friend-check.total-answers", { count: all })
		: t("widgets.friend-check.progress", {
				count: all,
				target: FRIEND_CHECK_TARGET,
			});

	if (coreValuesCount === CHOSEN_CORE_VALUES_LIMIT) {
		return <></>;
	}

	return (
		<div className="bg-brown-100 flex flex-col gap-2 rounded-3xl p-4">
			<IonText className="text-brown-700 text-2xl font-bold">
				{t("widgets.friend-check.title")}
			</IonText>
			<IonText className="text-brown-600">{t("widgets.friend-check.description")}</IonText>
			<div className="border-brown-200 rounded-2xl border-2 p-3">
				<IonText className="text-brown-700 text-sm font-semibold">{progressLabel}</IonText>
				<ProgressBar value={all / FRIEND_CHECK_TARGET} />
			</div>
			<IonButton
				onClick={() => router.push(friendCheckService.router.dashboard(myData))}
				className="ion-py-2.5 ion-rounded-lg min-h-10"
			>
				{all >= FRIEND_CHECK_TARGET
					? t("widgets.friend-check.action-finished")
					: t("widgets.friend-check.action")}
				<IonIcon slot="end" icon={arrowRightIcon} className="ml-2 size-5 opacity-60" />
			</IonButton>
		</div>
	);
}
